// Gestimmo — Plateforme de gestion immobilière 360 — Conçue par ELOUNGOU ÉTIENNE LANDRY
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, PrefsProvider, useAuth, usePrefs } from "@/lib/context";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Signin";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Properties from "@/pages/Properties";
import PropertyDetail from "@/pages/PropertyDetail";
import Leases from "@/pages/Leases";
import PaymentResult from "@/pages/PaymentResult";
import Tickets from "@/pages/Tickets";
import TicketDetail from "@/pages/TicketDetail";
import Faq from "@/pages/Faq";
import Admin from "@/pages/Admin";
import ProviderProfile from "@/pages/ProviderProfile";
import Finance from "@/pages/Finance";
import Charges from "@/pages/Charges";
import Contracts from "@/pages/Contracts";
import Documents from "@/pages/Documents";
import Concierge from "@/pages/Concierge";
import Maintenance from "@/pages/Maintenance";
import Reports from "@/pages/Reports";
import Planning from "@/pages/Planning";
import LiveTracking from "@/pages/LiveTracking";
import PublicTrack from "@/pages/PublicTrack";

const MGMT = ["admin", "supervision", "gestionnaire", "proprietaire"];

function Loading() {
  return <div className="flex min-h-screen items-center justify-center font-display text-2xl tracking-[0.2em] text-slate-300" data-testid="app-loading">GESTIMMO</div>;
}

function Pending() {
  const { user, logout } = useAuth();
  const { t } = usePrefs();
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F8FAFC] px-6" data-testid="pending-screen">
      <div className="rise max-w-md rounded-sm border border-slate-200 bg-white p-8">
        <div className="font-display text-xl font-bold tracking-[0.12em]">GESTIMMO</div>
        <h1 className="mt-6 font-display text-3xl font-bold">{t("pendingTitle")}</h1>
        <p className="mt-3 text-sm text-slate-600">{t("pendingText")}</p>
        <p className="mt-4 text-xs text-slate-500">{user.name} · {user.email} · {t(`role_${user.role}`)}</p>
        <button onClick={logout} className="mt-6 text-sm font-semibold text-[#996515] hover:underline" data-testid="pending-logout-button">{t("logout")}</button>
        <p className="mt-8 text-[11px] italic text-slate-400">Conçue par ELOUNGOU ÉTIENNE LANDRY</p>
      </div>
    </div>
  );
}

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.account_status === "pending") return <Pending />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/suivi/:token" element={<PublicTrack />} />
      <Route path="/login" element={user ? <Navigate to="/" replace /> : user === null ? <Loading /> : <Login />} />
      <Route element={<Protected><AppShell /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="properties" element={<Protected roles={["admin", "supervision", "gestionnaire", "proprietaire"]}><Properties /></Protected>} />
        <Route path="properties/:id" element={<PropertyDetail />} />
        <Route path="leases" element={<Leases />} />
        <Route path="payment/success" element={<PaymentResult />} />
        <Route path="payment/cancel" element={<PaymentResult cancelled />} />
        <Route path="tickets" element={<Tickets />} />
        <Route path="tickets/:id" element={<TicketDetail />} />
        <Route path="faq" element={<Faq />} />
        <Route path="finance" element={<Finance />} />
        <Route path="concierge" element={<Concierge />} />
        <Route path="documents" element={<Protected roles={[...MGMT, "locataire"]}><Documents /></Protected>} />
        <Route path="charges" element={<Protected roles={MGMT}><Charges /></Protected>} />
        <Route path="contracts" element={<Protected roles={MGMT}><Contracts /></Protected>} />
        <Route path="maintenance" element={<Protected roles={MGMT}><Maintenance /></Protected>} />
        <Route path="tracking" element={<LiveTracking />} />
        <Route path="planning" element={<Protected roles={["admin", "supervision", "gestionnaire", "prestataire"]}><Planning /></Protected>} />
        <Route path="reports" element={<Protected roles={MGMT}><Reports /></Protected>} />
        <Route path="provider" element={<Protected roles={["prestataire"]}><ProviderProfile /></Protected>} />
        <Route path="admin" element={<Protected roles={["admin", "supervision"]}><Admin /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <PrefsProvider>
          <AuthProvider>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </PrefsProvider>
      </BrowserRouter>
    </div>
  );
}
